import { CircleCheck, Copy, ExternalLink, Eye, EyeOff, Lightbulb } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { errorMessage } from "@/api/errors";
import { useConfirmBatch, usePrototypeBatches, type PrototypeBatch, type PrototypeShare } from "@/api/hooks/prototypes";
import { Field, FormError } from "@/components/Field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { currentVersion } from "@/features/prototypes/shares";
import { formatDate } from "@/lib/dates";
import { Markdown } from "@/lib/markdown";
import type { Role } from "./projectContext";

const NOTE_MAX = 1000;

const HOW_TO = ["how1", "how2", "how3", "how4", "how5"] as const;

function AccessCode({ code }: { code: string }) {
  const { t } = useTranslation();
  const labelId = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="font-medium">
        {t("prototype.code")}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <code aria-labelledby={labelId} className="rounded-md bg-surface-muted px-3 py-2 font-mono text-lg tracking-widest">
          {shown ? code : <span aria-label={t("prototype.codeHidden")}>{"•".repeat(Math.max(4, code.length))}</span>}
        </code>
        <Button size="sm" variant="ghost" aria-pressed={shown} onClick={() => setShown((s) => !s)}>
          {shown ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
          {shown ? t("prototype.hide") : t("prototype.show")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void navigator.clipboard?.writeText(code).then(() => toast.success(t("prototype.copied")))}
        >
          <Copy aria-hidden="true" className="h-4 w-4" />
          {t("prototype.copy")}
        </Button>
      </div>
    </div>
  );
}

function HowTo() {
  const { t } = useTranslation();
  return (
    <div className="rounded-md bg-secondary-subtle px-4 py-4">
      <h3 className="mb-2 flex items-center gap-2 text-base">
        <Lightbulb aria-hidden="true" className="h-5 w-5 shrink-0" />
        {t("prototype.howTitle")}
      </h3>
      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
        {HOW_TO.map((k) => (
          <li key={k}>{t(`prototype.${k}`)}</li>
        ))}
      </ul>
    </div>
  );
}

/** "Confirmed on 3 Nov 2026" plus who confirmed and, when staff recorded it, how the client told Codemonk. */
function ConfirmedStatus({ share }: { share: PrototypeShare }) {
  const { t } = useTranslation();
  const by = share.confirmed_by_name ? t("prototype.confirmedBy", { name: share.confirmed_by_name }) : "";
  const via =
    share.confirmed_via === "staff" && share.confirmation_method
      ? t("prototype.confirmedVia", { how: t(`prototype.method.${share.confirmation_method}`) })
      : "";
  const who = [by, via].filter(Boolean).join(" ");
  return (
    <>
      <Badge variant="success">
        <CircleCheck aria-hidden="true" className="h-4 w-4" />
        {t("prototype.confirmedOn", { date: formatDate(share.confirmed_at) })}
      </Badge>
      {who ? <span className="text-sm text-fg-muted">{who}</span> : null}
    </>
  );
}

function ConfirmDialog({
  org,
  projectSlug,
  batch,
  label,
  onClose,
}: {
  org: string;
  projectSlug: string;
  batch: PrototypeBatch;
  label: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmBatch(org, projectSlug);
  const [note, setNote] = useState("");
  const submit = () =>
    confirm.mutate(
      { batchId: batch.id, note: note.trim() || null },
      {
        onSuccess: () => {
          toast.success(t("prototype.confirmed"));
          onClose();
        },
      },
    );
  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("prototype.confirmTitle", { label })}</DialogTitle>
          <DialogDescription>{t("prototype.confirmBody")}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Field label={t("prototype.confirmNote")}>
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                maxLength={NOTE_MAX}
                placeholder={t("prototype.confirmNotePlaceholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <FormError message={confirm.isError ? errorMessage(confirm.error, t("errors.generic")) : null} />
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={confirm.isPending}>
              {t("prototype.confirmSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EarlierVersions({ versions }: { versions: PrototypeShare[] }) {
  const { t } = useTranslation();
  /** The version that replaced `v`: the next higher one the client can see. */
  const replacedBy = (v: PrototypeShare) =>
    Math.min(...versions.filter((o) => o.version > v.version).map((o) => o.version));
  return (
    <details className="rounded-md border border-border px-4 py-3">
      <summary className="cursor-pointer font-medium text-heading">{t("prototype.earlierVersions")}</summary>
      <ul className="mt-3 flex flex-col divide-y divide-border">
        {versions.slice(1).map((v) => (
          <li key={v.id} className="flex flex-col gap-1 py-3">
            <span className="font-semibold">{t("prototype.label", { batch: v.batch_number, version: v.version })}</span>
            <span className="text-sm text-fg-muted">{t("prototype.sharedOn", { date: formatDate(v.created_at) })}</span>
            <div className="flex flex-wrap items-center gap-2">
              {v.confirmed_at ? (
                <ConfirmedStatus share={v} />
              ) : (
                <Badge variant="grey">{t("prototype.replacedBy", { version: replacedBy(v) })}</Badge>
              )}
            </div>
            <Markdown source={v.message} className="text-sm" />
          </li>
        ))}
      </ul>
    </details>
  );
}

/** One batch as the client sees it: its current version, how to confirm it, and earlier versions. */
export function PrototypeBatchCard({
  org,
  projectSlug,
  batch,
  role,
  newest = false,
}: {
  org: string;
  projectSlug: string;
  batch: PrototypeBatch;
  role: Role;
  /** The newest batch also shows the access code and the how-to box. */
  newest?: boolean;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const hintId = useId();
  const [confirming, setConfirming] = useState(false);
  const versions = [...batch.versions].sort((a, b) => b.version - a.version);
  const current = currentVersion(versions);
  if (!current) return null;
  const label = t("prototype.label", { batch: batch.number, version: current.version });
  const earlierConfirmed = !current.confirmed_at && versions.some((v) => v.version < current.version && v.confirmed_at);
  const canConfirm = !current.confirmed_at && (role === "owner" || role === "member");
  return (
    <section aria-labelledby={titleId}>
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <h2 id={titleId} className="text-xl">
              {label}
            </h2>
            <p className="text-sm text-fg-muted">{t("prototype.sharedOn", { date: formatDate(current.created_at) })}</p>
            <div className="flex flex-wrap items-center gap-2">
              {current.confirmed_at ? <ConfirmedStatus share={current} /> : <Badge>{t("prototype.current")}</Badge>}
            </div>
          </div>
          <Button asChild className="shrink-0">
            <a href={current.url} target="_blank" rel="noopener noreferrer" className="no-underline">
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
              {t("prototype.open")}
            </a>
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {newest && current.access_code ? <AccessCode code={current.access_code} /> : null}
          <Markdown source={current.message} />
          {earlierConfirmed ? (
            <p className="text-sm">
              {t("prototype.reconfirm", { version: current.version, date: formatDate(current.created_at) })}
            </p>
          ) : null}
          {canConfirm ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                disabled={role !== "owner"}
                aria-describedby={role === "owner" ? undefined : hintId}
                onClick={() => setConfirming(true)}
              >
                <CircleCheck aria-hidden="true" className="h-4 w-4" />
                {t("prototype.confirm")}
              </Button>
              {role === "owner" ? null : (
                <span id={hintId} className="text-sm text-fg-muted">
                  {t("prototype.ownerOnly")}
                </span>
              )}
            </div>
          ) : null}
          {newest ? <HowTo /> : null}
          {versions.length > 1 ? <EarlierVersions versions={versions} /> : null}
        </CardContent>
      </Card>
      {confirming ? (
        <ConfirmDialog org={org} projectSlug={projectSlug} batch={batch} label={label} onClose={() => setConfirming(false)} />
      ) : null}
    </section>
  );
}

/** Project overview: one card per prototype batch, newest batch first. Nothing until a batch is shared. */
export function PrototypeCard({ org, projectSlug, role }: { org: string; projectSlug: string; role: Role }) {
  const batches = usePrototypeBatches(org, projectSlug);
  const visible = [...(batches.data ?? [])]
    .filter((b) => currentVersion(b.versions))
    .sort((a, b) => b.number - a.number);
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-col gap-6">
      {visible.map((b, i) => (
        <PrototypeBatchCard key={b.id} org={org} projectSlug={projectSlug} batch={b} role={role} newest={i === 0} />
      ))}
    </div>
  );
}
